import { type Ref, useCallback, useEffect, useMemo } from "react"
import styled from "styled-components"

import {
  useCreateVendorContractorPartnerMutation,
  useDeleteVendorContractorPartnerMutation,
  useLazyGetContractorQuery,
  useUpdateVendorContractorPartnerMutation,
} from "store/api/contractorsApi"
import { useLazyGetPartnerCategoriesQuery } from "store/api/dictionaryApi"
import { UITypes } from "types"

import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import { ContractorPartnersForm, type ContractorPartnersFormHandle } from "components/ContractorPartnersForm"

const Container = styled.div`
  padding: 0 18px;
`

type Props = {
  ref?: Ref<ContractorPartnersFormHandle>
  contractor: UITypes.Contractor
  isCreate?: boolean
}

export function PartnersSection({ ref, contractor, isCreate }: Props) {
  const [triggerGetPartnerCategories, { data: categoriesData }] = useLazyGetPartnerCategoriesQuery()
  const [createVendorPartner, { isLoading: isCreating }] = useCreateVendorContractorPartnerMutation()
  const [updateVendorPartner, { isLoading: isUpdating }] = useUpdateVendorContractorPartnerMutation()
  const [deleteVendorPartner, { isLoading: isDeleting }] = useDeleteVendorContractorPartnerMutation()
  const [triggerGetContractor] = useLazyGetContractorQuery()

  useEffect(() => {
    triggerGetPartnerCategories()
  }, [triggerGetPartnerCategories])

  const partnerCategories = categoriesData?.data ?? []
  const initialPartners = useMemo(() => contractor.contractorPartners ?? [], [contractor.contractorPartners])

  const resolvePartnerApi = useCallback(
    (contractorId: number) => ({
      createPartner: (body: FormData) => createVendorPartner({ contractorId, body }).unwrap(),
      updatePartner: (partnerId: number, body: FormData) =>
        updateVendorPartner({ contractorId, partnerId, body }).unwrap(),
      deletePartner: (partnerId: number) => deleteVendorPartner({ contractorId, partnerId }).unwrap(),
      onPartnerChangesSettled: async () => {
        await triggerGetContractor(String(contractorId))
      },
    }),
    [createVendorPartner, deleteVendorPartner, triggerGetContractor, updateVendorPartner],
  )

  const isLoading = !categoriesData
  const isSaving = isCreating || isUpdating || isDeleting

  return (
    <>
      <ModalSectionTitle title="Partners" />
      <Container>
        <ContractorPartnersForm
          ref={ref}
          key={`partners-${isCreate ? "new" : (contractor.id ?? "unknown")}`}
          variant="embedded"
          title="Partners"
          categories={partnerCategories}
          excludeFirstCategory
          isLoading={isLoading}
          initialPartners={initialPartners}
          isSaving={isSaving}
          resolvePartnerApi={resolvePartnerApi}
          silentPartnerToasts
        />
      </Container>
    </>
  )
}
